/** Shared helpers for OpenAI catalog auto-translate requests. */

import { SUPPORTED_LOCALES, type SupportedLocale } from "@/lib/locales";
import type {
  AutoTranslateCatalogFieldsRequest,
  AutoTranslateCatalogFieldsResponse,
} from "@/services/catalogTranslationService";
import type { TranslationFormValues } from "@/lib/translationResolution";
import {
  findMissingTranslations,
  hasPartialTranslations,
} from "@/lib/translationResolution";

/**
 * Apply API translations onto the current form values.
 * By default only fills empty locales. Pass `overwrite: true` to replace
 * existing text (used after rename when every locale is already filled).
 */
export function mergeTranslationResults(
  current: TranslationFormValues,
  incoming: TranslationFormValues,
  options: { overwrite?: boolean } = {},
): TranslationFormValues {
  const merged = { ...current };
  const overwrite = options.overwrite === true;
  for (const code of SUPPORTED_LOCALES) {
    const next = incoming[code]?.trim();
    if (!next) continue;
    if (overwrite || !merged[code]?.trim()) {
      merged[code] = next;
    }
  }
  return merged;
}

/** Copy the selected locale string into every supported language (no AI). */
export function applyValueToAllLocales(
  values: TranslationFormValues,
  sourceLanguage: SupportedLocale,
): TranslationFormValues {
  const source = values[sourceLanguage] ?? "";
  const next: TranslationFormValues = { ...values };
  for (const code of SUPPORTED_LOCALES) {
    next[code] = source;
  }
  return next;
}

function otherCatalogLocales(
  sourceLanguage: SupportedLocale,
): SupportedLocale[] {
  return SUPPORTED_LOCALES.filter((code) => code !== sourceLanguage);
}

export function descriptionNeedsAutoTranslate(
  values: TranslationFormValues,
): boolean {
  if (!SUPPORTED_LOCALES.some((code) => values[code]?.trim())) {
    return false;
  }
  return (
    hasPartialTranslations(values) || findMissingTranslations(values).length > 0
  );
}

export function buildAutoTranslateRequest(
  sourceLanguage: SupportedLocale,
  nameTranslations: TranslationFormValues,
  descriptionTranslations: TranslationFormValues,
  options: { includeDescription: boolean },
): AutoTranslateCatalogFieldsRequest {
  const name = nameTranslations[sourceLanguage]?.trim() ?? "";
  const description = descriptionTranslations[sourceLanguage]?.trim() ?? "";

  const missingNameLocales = findMissingTranslations(nameTranslations).filter(
    (code) => code !== sourceLanguage,
  );
  const missingDescLocales = options.includeDescription
    ? findMissingTranslations(descriptionTranslations).filter(
        (code) => code !== sourceLanguage,
      )
    : [];

  const targetSet = new Set<SupportedLocale>([
    ...missingNameLocales,
    ...missingDescLocales,
  ]);
  const target_languages =
    targetSet.size > 0 ? [...targetSet] : otherCatalogLocales(sourceLanguage);

  return {
    source_language: sourceLanguage,
    target_languages,
    name,
    description: options.includeDescription ? description : "",
  };
}

export function applyAutoTranslateResult(
  nameTranslations: TranslationFormValues,
  descriptionTranslations: TranslationFormValues,
  result: AutoTranslateCatalogFieldsResponse,
  options: { mergeDescriptions: boolean },
): {
  nameTranslations: TranslationFormValues;
  descriptionTranslations: TranslationFormValues;
} {
  const nameMissing = findMissingTranslations(nameTranslations).length > 0;
  const descriptionMissing =
    options.mergeDescriptions &&
    findMissingTranslations(descriptionTranslations).length > 0;
  // Only overwrite when every required field is already complete — rename/refresh.
  // Otherwise keep fill-empty so filling missing descriptions cannot clobber names
  // (the API always returns name translations for the requested targets).
  const overwrite = !nameMissing && !descriptionMissing;

  return {
    nameTranslations: mergeTranslationResults(
      nameTranslations,
      result.name_translations,
      { overwrite },
    ),
    descriptionTranslations:
      options.mergeDescriptions && result.description_translations
        ? mergeTranslationResults(
            descriptionTranslations,
            result.description_translations,
            { overwrite },
          )
        : descriptionTranslations,
  };
}
